import ax from "axios";
import type { AxiosResponse } from "axios";
import type {
  UserDeactivate,
  // RegisteredEventInfo,
  EventResponse,
  LoginReq,
  Token,
  PasswordRecovery,
  NewPassword,
  SanitizedUserList,
  UserCreate,
  SanitizedUser,
  UserUpdateMe,
  UpdatePassword,
  UserRegister,
  UserUpdate,
  FormSubmissionPayload,
  FormSubmissionBase,
  FormSubmission,
  BglFile,
  BglFilePatch,
  NoteCreate,
  NoteWithReplies,
  AutomationResponse,
  AutomationCreate,
  AutomationUpdate,
  AutomationExecutionResponse,
  TestAutomationRequest,
  ActionTypeSchema,
  OperatorSchema,
  PaymentIntentCreateRequest,
  PaymentIntentResponse,
  PaymentMethodSetupRequest,
  PaymentMethodSetupResponse,
  PaymentMethodCaptureRequest,
  PaymentMethodCaptureResponse,
  PaymentMethodSaveResponse,
  PaymentMethodResponse,
  PaymentMethodDeleteResponse,
  TransactionStatsResponse,
  RecurringPaymentStatsResponse,
  PaymentMethodStatsResponse,
  RefundRequest,
  RefundResponse,
  ChargeTokenRequest,
  TransactionChargeResponse,
  RecurringPaymentInterval,
  RecurringPaymentResponse,
  RecurringPaymentCreateRequest,
  RecurringPaymentUpdateRequest,
  RecurringPaymentDeleteResponse,
  RecurringPaymentCancelResponse,
  RecurringPaymentBillingResult,
  RecurringPaymentChargeResponse,
  PaymentLog,
  ProviderListResponse,
  HealthCheckResponse,
  Transaction,
  MembershipPlanCreate,
  MembershipPlanResponse,
  MembershipPlanUpdate,
  MembershipGroupCreate,
  MembershipGroupResponse,
  MembershipStatus,
  MembershipGroupWithPlanResponse,
  MembershipGroupUpdate,
  AddMemberToGroupRequest,
  MembershipResponse,
  // RemoveMemberFromGroupRequest,
  MembershipCancelResponse,
  MembershipCreate,
  MembershipWithPlanResponse,
  MembershipWithDetailsResponse,
  MembershipUpdate,
  MembershipCancelRequest,
  ExpiringMembershipsRequest,
  ExpiringMembershipsResponse,
  BulkMembershipAction,
  BulkMembershipResponse,
  EntitlementCheckRequest,
  EntitlementCheckResponse,
  EmailAccount,
  EmailTemplate,
  SendEmailEmailSend,
  EmailMessage,
  PipelineRead,
  PipelineBase,
  CreateStageRequest,
  StageRead,
  PipelineItemRead,
  UpdateItemRequest,
  CreateItemRequest,
  MoveItemRequest,
  CreateTagRequest,
  PipelineTag,
  TagActionResponse,
  PersonCreate,
  PersonWithRelations,
  PersonWithEmail,
  PersonBase,
  Email,
  Phone,
  Address,
  AddressWrite,
  DuplicateGroup,
  MergePersonsRequest,
  AdvertRead,
  AdvertBase,
  ProductRead,
  ProductUpdate,
  Product,
  ProductBase,
  Cart,
  CartWithItems,
  CartItem,
  ShippingOptionRead,
  ShippingOption,
  OrderRead,
  OrderUpdate,
  Order,
  OrderStatus,
  Promotion,
  PromotionBase,
  Coupon,
  CouponBase,
  CouponValidationResponse,
  ShippingInformation,
  ProductCategory,
  BundleRead,
  BundleCreate,
  BundleUpdate,
  AppliedBundleInfo,
  SalesOverviewAnalytics,
  ProductPerformanceAnalytics,
  RevenueTrendsAnalytics,
  DailyBreakdownAnalytics,
  CartAnalytics,
  CouponPerformanceAnalytics,
  CategoryPerformanceAnalytics,
  CustomerAnalytics,
  Author,
  AuthorBase,
  LibPaginationReq,
  LibItemWithJoinsRes,
  GetAvailableAppLibItemsReq,
  GetAvailableAppLibItemsJoinRes,
  LibItemUpsert,
  AddCollectionReq,
  LibCategory,
  LibCategoryBase,
  LibCollectionItemBase,
  LibCollectionItem,
  PersonToLibItemLinkBase,
  PersonWithLibItems,
  UpdateProgressReq,
  SignupReq,
  PersonSubscriptionLink,
  Subscription,
} from "./types.d";

/**
 * Options for file upload operations
 */
export interface UploadOptions {
  /** Progress callback for upload operations */
  onUploadProgress?: (progressEvent: any) => void;
  /** Directory path for the uploaded file */
  dirPath?: string;
  /** Tags to associate with the uploaded file */
  tags?: string[];
}

/**
 * Configured axios instance for API requests
 * @example
 * // Making a direct request with the axios instance
 * const response = await axios.get('/some-endpoint');
 */
export const axios = ax.create({
  baseURL: import.meta.env.VITE_BAGEL_BASE_URL,
  withCredentials: true,
});

/**
 * API functions for checkDb endpoints
 */
export const checkDb =
  /**
   * Db Check
   *
   * @endpoint GET /check-db
   */
  async (): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.get("/check-db");
  };

/**
 * API functions for checkAsyncDb endpoints
 */
export const checkAsyncDb =
  /**
   * Async Db Check
   *
   * @endpoint GET /check-async-db
   */
  async (): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.get("/check-async-db");
  };

/**
 * API functions for sentryDebug endpoints
 */
export const sentryDebug =
  /**
   * Trigger Division By Zero Error
   *
   * @endpoint GET /sentry-debug
   */
  async (): Promise<AxiosResponse<number>> => {
    return axios.get("/sentry-debug");
  };

/**
 * API functions for migdalorUsers endpoints
 */
export const migdalorUsers = {
  /**
   * Delete Migdalor App User
   *
   * @endpoint PATCH /migdalor_users/me
   */
  me: async ({ userDeactivate }: { userDeactivate?: UserDeactivate } = {}) => {
    return axios.patch("/migdalor_users/me", userDeactivate);
  },
};

/**
 * API functions for api endpoints
 */
export const api = {
  membership: {
    /**
     * Migrate Membership
     *
     * Analyze recurring payments and compile a list of memberships to create.
     * Does NOT commit to the database - just returns the analysis.
     *
     * @endpoint POST /api/membership/migrate
     */
    migrate: async () => {
      return axios.post("/api/membership/migrate", {});
    },
    /**
     * Export Members
     *
     * Export members based on RecurringPayments as a list of dicts.
     * Only includes active/trialing recurring payments.
     * Includes membership plan name and status.
     *
     * @endpoint GET /api/membership/export-members
     */
    exportMembers: async (): Promise<
      AxiosResponse<{ [key: string]: any }[]>
    > => {
      return axios.get("/api/membership/export-members");
    },
  },
  /**
   * Fix Addresses
   *
   * Loads the orders, checks each order's person's address, and if the person only has one address,
   * updates any missing address information in that address from data on the order (if possible).
   * Only adds missing data, does not replace existing data. Confirms city name matches before
   * copying over additional address details.
   *
   * @endpoint POST /api/fix-addresses
   */
  fixAddresses: async (): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.post("/api/fix-addresses", {});
  },
};

/**
 * API functions for events endpoints
 */
export const events = {
  log: {
    /**
     * Get Event Details
     *
     * Get details of a specific event execution.
     *
     * Returns the event data and all handler executions in one response.
     *
     * @endpoint GET /events/log/{event_id}
     */
    getByEventId: async ({ eventId }: { eventId?: string } = {}): Promise<
      AxiosResponse<EventResponse>
    > => {
      return axios.get(`/events/log/${eventId}`);
    },
    /**
     * Get Event Log
     *
     * Get event log with filtering.
     *
     * Shows all events that have been triggered, with optional filters:
     * - event_name: Filter by specific event
     * - source_module: Filter by module that emitted
     *
     * @endpoint GET /events/log
     */
    get: async ({
      eventName,
      sourceModule,
      limit = 50,
      offset,
    }: {
      eventName?: string;
      sourceModule?: string;
      limit?: number;
      offset?: number;
    } = {}): Promise<AxiosResponse<EventResponse[]>> => {
      return axios.get("/events/log", {
        params: {
          event_name: eventName,
          source_module: sourceModule,
          limit,
          offset,
        },
      });
    },
  },
  /**
   * Trigger Event
   *
   * Manually trigger an event (for testing).
   *
   * Validates the payload against the registered event schema before emitting.
   * Context is automatically generated.
   *
   * Example:
   *     POST /events/trigger/crawler.site_created
   *     {id: "123", name: "Test Site", base_url: "https://test.com"}
   *
   * @endpoint POST /events/trigger/{event_name}
   */
  trigger: async ({
    eventName,
    payload,
  }: { eventName?: string; payload?: { [key: string]: any } } = {}): Promise<
    AxiosResponse<{ [key: string]: any }>
  > => {
    return axios.post(`/events/trigger/${eventName}`, payload);
  },
};

/**
 * API functions for auth endpoints
 */
export const auth = {
  /**
   * Login
   *
   * @endpoint POST /auth/login
   */
  login: async ({ loginReq }: { loginReq?: LoginReq } = {}): Promise<
    AxiosResponse<Token>
  > => {
    return axios.post("/auth/login", loginReq);
  },
  /**
   * Recover Password
   *
   * @endpoint POST /auth/password-recovery
   */
  passwordRecovery: async ({
    passwordRecovery,
  }: { passwordRecovery?: PasswordRecovery } = {}) => {
    return axios.post("/auth/password-recovery", passwordRecovery);
  },
  /**
   * Reset Password
   *
   * @endpoint POST /auth/reset-password
   */
  resetPassword: async ({
    newPassword,
  }: { newPassword?: NewPassword } = {}) => {
    return axios.post("/auth/reset-password", newPassword);
  },
};

/**
 * API functions for users endpoints
 */
export const users = {
  /**
   * Get By Id
   *
   * @endpoint GET /users/{user_id}
   */
  getByUserId: async ({ userId }: { userId?: string } = {}): Promise<
    AxiosResponse<SanitizedUser>
  > => {
    return axios.get(`/users/${userId}`);
  },
  /**
   * Update
   *
   * @endpoint PATCH /users/{user_id}
   */
  patch: async ({
    userId,
    userUpdate,
  }: { userId?: string; userUpdate?: UserUpdate } = {}): Promise<
    AxiosResponse<SanitizedUser>
  > => {
    return axios.patch(`/users/${userId}`, userUpdate);
  },
  /**
   * Delete User
   *
   * @endpoint DELETE /users/{user_id}
   */
  delete: async ({ userId }: { userId?: string } = {}) => {
    return axios.delete(`/users/${userId}`);
  },
  /**
   * List Users
   *
   * @endpoint GET /users/
   */
  get: async ({
    skip,
    limit = 100,
  }: { skip?: number; limit?: number } = {}): Promise<
    AxiosResponse<SanitizedUserList>
  > => {
    return axios.get("/users/", { params: { skip, limit } });
  },
  /**
   * Create
   *
   * @endpoint POST /users/
   */
  post: async ({ userCreate }: { userCreate?: UserCreate } = {}): Promise<
    AxiosResponse<SanitizedUser>
  > => {
    return axios.post("/users/", userCreate);
  },
  me: {
    /**
     * User Details
     *
     * @endpoint GET /users/me
     */
    get: async (): Promise<AxiosResponse<SanitizedUser>> => {
      return axios.get("/users/me");
    },
    /**
     * Update Me
     *
     * @endpoint PATCH /users/me
     */
    patch: async ({
      userUpdateMe,
    }: { userUpdateMe?: UserUpdateMe } = {}): Promise<
      AxiosResponse<SanitizedUser>
    > => {
      return axios.patch("/users/me", userUpdateMe);
    },
    /**
     * Update My Password
     *
     * @endpoint PATCH /users/me/password
     */
    password: async ({
      updatePassword,
    }: { updatePassword?: UpdatePassword } = {}) => {
      return axios.patch("/users/me/password", updatePassword);
    },
  },
  /**
   * Register
   *
   * @endpoint POST /users/signup
   */
  signup: async ({
    userRegister,
  }: { userRegister?: UserRegister } = {}): Promise<
    AxiosResponse<SanitizedUser>
  > => {
    return axios.post("/users/signup", userRegister);
  },
};

/**
 * API functions for form endpoints
 */
export const form = {
  /**
   * Update Form
   *
   * Update a form submission.
   *
   * @endpoint PUT /form/{form_id}
   */
  put: async ({
    formId,
    formSubmissionPayload,
  }: {
    formId?: string;
    formSubmissionPayload?: FormSubmissionPayload;
  } = {}): Promise<AxiosResponse<FormSubmission>> => {
    return axios.put(`/form/${formId}`, formSubmissionPayload);
  },
  /**
   * Get By Id
   *
   * Get form submissions by form name.
   *
   * @endpoint GET /form/{form_name}
   */
  getByFormName: async ({
    formName,
    limit = 100,
    skip,
  }: { formName?: string; limit?: number; skip?: number } = {}): Promise<
    AxiosResponse<{ [key: string]: any }[]>
  > => {
    return axios.get(`/form/${formName}`, { params: { limit, skip } });
  },
  /**
   * Submit Form With Name
   *
   * Submit a form with a specific form name.
   *
   * @endpoint POST /form/{form_name}
   */
  postByFormName: async ({
    formName,
    formSubmissionPayload,
  }: {
    formName?: string;
    formSubmissionPayload?: FormSubmissionPayload;
  } = {}): Promise<AxiosResponse<FormSubmissionBase>> => {
    return axios.post(`/form/${formName}`, formSubmissionPayload);
  },
  /**
   * Get Form Names
   *
   * Get all form names.
   *
   * @endpoint GET /form
   */
  get: async (): Promise<AxiosResponse<string[]>> => {
    return axios.get("/form");
  },
  /**
   * Submit Form
   *
   * Submit a form.
   *
   * @endpoint POST /form
   */
  post: async ({
    formSubmissionPayload,
  }: { formSubmissionPayload?: FormSubmissionPayload } = {}): Promise<
    AxiosResponse<FormSubmissionBase>
  > => {
    return axios.post("/form", formSubmissionPayload);
  },
};

/**
 * API functions for filesV2 endpoints
 */
export const filesV2 = {
  /**
   * Get File
   *
   * @endpoint GET /files_v2/
   */
  get: async ({ pathKey }: { pathKey?: string } = {}): Promise<
    AxiosResponse<BglFile>
  > => {
    return axios.get("/files_v2/", { params: { path_key: pathKey } });
  },
  /**
   * Update File
   *
   * @endpoint PUT /files_v2/
   */
  put: async ({
    pathKey,
    bglFilePatch,
  }: { pathKey?: string; bglFilePatch?: BglFilePatch } = {}): Promise<
    AxiosResponse<BglFile>
  > => {
    return axios.put("/files_v2/", bglFilePatch, {
      params: { path_key: pathKey },
    });
  },
  /**
   * Delete File
   *
   * @endpoint DELETE /files_v2/
   */
  delete: async ({ pathKey }: { pathKey?: string } = {}) => {
    return axios.delete("/files_v2/", { params: { path_key: pathKey } });
  },
  /**
   * Upload Files
   *
   * @endpoint POST /files_v2/upload
   */
  upload: async (
    file: File,
    options?: UploadOptions & { dirPath?: string; tags?: string[] },
  ): Promise<AxiosResponse<BglFile>> => {
    const formData = new FormData();
    formData.append("upload", file);
    return axios.post("/files_v2/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: options?.onUploadProgress,
      params: { dir_path: options?.dirPath, tags: options?.tags },
    });
  },
  /**
   * Get Files List
   *
   * @endpoint GET /files_v2/list
   */
  list: async ({ dirPath }: { dirPath?: string } = {}): Promise<
    AxiosResponse<BglFile[]>
  > => {
    return axios.get("/files_v2/list", { params: { dir_path: dirPath } });
  },
};

/**
 * API functions for note endpoints
 */
export const note = {
  /**
   * Update Note
   *
   * @endpoint PUT /note/{note_id}
   */
  put: async ({
    noteId,
    content,
    category,
    extraData,
  }: {
    noteId?: string;
    content?: string;
    category?: string;
    extraData?: { [key: string]: any };
  } = {}): Promise<AxiosResponse<NoteWithReplies>> => {
    return axios.put(`/note/${noteId}`, extraData, {
      params: { content, category },
    });
  },
  /**
   * Delete Note
   *
   * @endpoint DELETE /note/{note_id}
   */
  delete: async ({ noteId }: { noteId?: string } = {}) => {
    return axios.delete(`/note/${noteId}`);
  },
  /**
   * Get Replies
   *
   * @endpoint GET /note/
   */
  getAll: async ({
    refId,
    type_,
    category,
  }: { refId?: string; type_?: string; category?: string } = {}): Promise<
    AxiosResponse<NoteWithReplies[]>
  > => {
    return axios.get("/note/", {
      params: { ref_id: refId, type: type_, category },
    });
  },
  /**
   * Create Note
   *
   * @endpoint POST /note/{ref_id}
   */
  post: async ({
    refId,
    noteCreate,
  }: { refId?: string; noteCreate?: NoteCreate } = {}): Promise<
    AxiosResponse<NoteWithReplies>
  > => {
    return axios.post(`/note/${refId}`, noteCreate);
  },
  /**
   * Get Entries
   *
   * @endpoint GET /note/{ref_id}
   */
  get: async ({
    refId,
    type_,
    category,
  }: { refId?: string; type_?: string; category?: string } = {}): Promise<
    AxiosResponse<NoteWithReplies[]>
  > => {
    return axios.get(`/note/${refId}`, { params: { type: type_, category } });
  },
};

/**
 * API functions for automations endpoints
 */
export const automations = {
  /**
   * Get Automation
   *
   * Get a specific automation
   *
   * @endpoint GET /automations/{automation_id}
   */
  getByAutomationId: async ({
    automationId,
  }: { automationId?: string } = {}): Promise<
    AxiosResponse<AutomationResponse>
  > => {
    return axios.get(`/automations/${automationId}`);
  },
  /**
   * Update Automation
   *
   * Update an automation
   *
   * @endpoint PUT /automations/{automation_id}
   */
  put: async ({
    automationId,
    automationUpdate,
  }: {
    automationId?: string;
    automationUpdate?: AutomationUpdate;
  } = {}): Promise<AxiosResponse<AutomationResponse>> => {
    return axios.put(`/automations/${automationId}`, automationUpdate);
  },
  /**
   * Delete Automation
   *
   * Delete an automation
   *
   * @endpoint DELETE /automations/{automation_id}
   */
  delete: async ({ automationId }: { automationId?: string } = {}) => {
    return axios.delete(`/automations/${automationId}`);
  },
  /**
   * List Automations
   *
   * List all automations
   *
   * @endpoint GET /automations
   */
  get: async ({
    enabledOnly,
    triggerEvent,
    limit = 50,
    offset,
  }: {
    enabledOnly?: boolean;
    triggerEvent?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<AxiosResponse<AutomationResponse[]>> => {
    return axios.get("/automations", {
      params: {
        enabled_only: enabledOnly,
        trigger_event: triggerEvent,
        limit,
        offset,
      },
    });
  },
  /**
   * Create Automation
   *
   * Create a new automation
   *
   * @endpoint POST /automations
   */
  post: async ({
    automationCreate,
  }: { automationCreate?: AutomationCreate } = {}): Promise<
    AxiosResponse<AutomationResponse>
  > => {
    return axios.post("/automations", automationCreate);
  },
  /**
   * Toggle Automation
   *
   * Toggle automation enabled/disabled
   *
   * @endpoint PATCH /automations/{automation_id}/toggle
   */
  toggle: async ({ automationId }: { automationId?: string } = {}): Promise<
    AxiosResponse<AutomationResponse>
  > => {
    return axios.patch(`/automations/${automationId}/toggle`, {});
  },
  executions: {
    /**
     * Get Execution
     *
     * Get a specific execution
     *
     * @endpoint GET /automations/executions/{execution_id}
     */
    getByExecutionId: async ({
      executionId,
    }: { executionId?: string } = {}): Promise<
      AxiosResponse<AutomationExecutionResponse>
    > => {
      return axios.get(`/automations/executions/${executionId}`);
    },
    /**
     * Get Automation Executions
     *
     * Get execution history for an automation
     *
     * @endpoint GET /automations/{automation_id}/executions
     */
    get: async ({
      automationId,
      limit = 50,
      offset,
    }: {
      automationId?: string;
      limit?: number;
      offset?: number;
    } = {}): Promise<AxiosResponse<AutomationExecutionResponse[]>> => {
      return axios.get(`/automations/${automationId}/executions`, {
        params: { limit, offset },
      });
    },
  },
  /**
   * Test Automation
   *
   * Test an automation with sample data
   *
   * @endpoint POST /automations/{automation_id}/test
   */
  test: async ({
    automationId,
    testAutomationRequest,
  }: {
    automationId?: string;
    testAutomationRequest?: TestAutomationRequest;
  } = {}): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.post(
      `/automations/${automationId}/test`,
      testAutomationRequest,
    );
  },
  actions: {
    /**
     * Get Action Types
     *
     * Get all available action types
     *
     * @endpoint GET /automations/actions/all
     */
    all: async (): Promise<AxiosResponse<ActionTypeSchema[]>> => {
      return axios.get("/automations/actions/all");
    },
    /**
     * Get Action Schema
     *
     * Get schema for a specific action type
     *
     * @endpoint GET /automations/actions/{action_type}/schema
     */
    schema: async ({ actionType }: { actionType?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.get(`/automations/actions/${actionType}/schema`);
    },
  },
  operators: {
    /**
     * Get Operators
     *
     * Get all available condition operators
     *
     * @endpoint GET /automations/operators/all
     */
    all: async (): Promise<AxiosResponse<OperatorSchema[]>> => {
      return axios.get("/automations/operators/all");
    },
  },
  stats: {
    /**
     * Get Automation Stats
     *
     * Get automation statistics
     *
     * @endpoint GET /automations/stats/overview
     */
    overview: async (): Promise<AxiosResponse<{ [key: string]: any }>> => {
      return axios.get("/automations/stats/overview");
    },
  },
};

/**
 * API functions for payment endpoints
 */
export const payment = {
  /**
   * Create Payment Intent Default
   *
   * Create a payment intent using the default provider.
   *
   * @endpoint POST /payment/create
   */
  create: async ({
    paymentIntentCreateRequest,
  }: { paymentIntentCreateRequest?: PaymentIntentCreateRequest } = {}): Promise<
    AxiosResponse<PaymentIntentResponse>
  > => {
    return axios.post("/payment/create", paymentIntentCreateRequest);
  },
  paymentMethods: {
    /**
     * Delete Payment Method
     *
     * Delete a payment method.
     *
     * @endpoint DELETE /payment/payment-methods/{payment_method_id}
     */
    delete: async ({
      paymentMethodId,
    }: { paymentMethodId?: string } = {}): Promise<
      AxiosResponse<PaymentMethodDeleteResponse>
    > => {
      return axios.delete(`/payment/payment-methods/${paymentMethodId}`);
    },
    /**
     * Get Payment Method
     *
     * Get a payment method.
     *
     * @endpoint GET /payment/payment-methods/{payment_method_id}
     */
    getByPaymentMethodId: async ({
      paymentMethodId,
    }: { paymentMethodId?: string } = {}): Promise<
      AxiosResponse<PaymentMethodResponse>
    > => {
      return axios.get(`/payment/payment-methods/${paymentMethodId}`);
    },
    /**
     * Save Payment Method Default
     *
     * Save a payment method using the default provider.
     *
     * @endpoint POST /payment/payment-methods
     */
    post: async ({
      personId,
      token,
      isDefault,
    }: {
      personId?: string;
      token?: string;
      isDefault?: boolean;
    } = {}): Promise<AxiosResponse<PaymentMethodSaveResponse>> => {
      return axios.post(
        "/payment/payment-methods",
        {},
        { params: { person_id: personId, token, is_default: isDefault } },
      );
    },
    /**
     * List Payment Methods
     *
     * List all payment methods across all customers.
     *
     * Args:
     *     db: Database session
     *     limit: Maximum number of payment methods to return
     *     offset: Number of payment methods to skip
     *     person_id: Filter by person
     *     provider_id: Filter by provider
     *
     * Returns:
     *     List of payment methods with associated person information
     *
     * @endpoint GET /payment/payment-methods
     */
    get: async ({
      limit = 50,
      offset,
      personId,
      providerId,
    }: {
      limit?: number;
      offset?: number;
      personId?: string;
      providerId?: string;
    } = {}): Promise<AxiosResponse<PaymentMethodResponse[]>> => {
      return axios.get("/payment/payment-methods", {
        params: { limit, offset, person_id: personId, provider_id: providerId },
      });
    },
    /**
     * Create Payment Method Setup
     *
     * Create a setup for capturing payment method without charging.
     *
     * This endpoint creates a setup intent (Stripe) or equivalent for other providers
     * to capture payment method information without making a charge.
     *
     * @endpoint POST /payment/payment-methods/setup
     */
    setup: async ({
      paymentMethodSetupRequest,
    }: { paymentMethodSetupRequest?: PaymentMethodSetupRequest } = {}): Promise<
      AxiosResponse<PaymentMethodSetupResponse>
    > => {
      return axios.post(
        "/payment/payment-methods/setup",
        paymentMethodSetupRequest,
      );
    },
    /**
     * Capture Payment Method
     *
     * Capture and save payment method from card details.
     *
     * This endpoint is primarily for PayPlus and other providers that accept
     * card details directly. For Stripe, use the setup intent flow instead.
     *
     * @endpoint POST /payment/payment-methods/capture
     */
    capture: async ({
      paymentMethodCaptureRequest,
    }: {
      paymentMethodCaptureRequest?: PaymentMethodCaptureRequest;
    } = {}): Promise<AxiosResponse<PaymentMethodCaptureResponse>> => {
      return axios.post(
        "/payment/payment-methods/capture",
        paymentMethodCaptureRequest,
      );
    },
    /**
     * Validate a payment method token
     *
     * Validate a payment method by ID or raw token - always auto-detects provider
     *
     * @endpoint POST /payment/payment-methods/validate/{identifier}
     */
    validate: async ({ identifier }: { identifier?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.post(`/payment/payment-methods/validate/${identifier}`, {});
    },
    /**
     * Get Payment Method Stats
     *
     * Get payment method stats and quality signals.
     *
     * @endpoint GET /payment/payment-methods/stats
     */
    stats: async (): Promise<AxiosResponse<PaymentMethodStatsResponse>> => {
      return axios.get("/payment/payment-methods/stats");
    },
  },
  transactions: {
    /**
     * Get Transaction By Id
     *
     * Get a specific transaction by ID.
     *
     * @endpoint GET /payment/transactions/{transaction_id}
     */
    getByTransactionId: async ({
      transactionId,
    }: { transactionId?: string } = {}): Promise<
      AxiosResponse<Transaction>
    > => {
      return axios.get(`/payment/transactions/${transactionId}`);
    },
    /**
     * List All Transactions
     *
     * List all transactions across all customers.
     *
     * @endpoint GET /payment/transactions
     */
    get: async ({
      limit = 50,
      offset,
    }: { limit?: number; offset?: number } = {}): Promise<
      AxiosResponse<Transaction[]>
    > => {
      return axios.get("/payment/transactions", { params: { limit, offset } });
    },
    /**
     * Get Transaction Stats
     *
     * Get transaction stats.
     *
     * @endpoint GET /payment/transactions/stats
     */
    stats: async (): Promise<AxiosResponse<TransactionStatsResponse>> => {
      return axios.get("/payment/transactions/stats");
    },
    /**
     * Refund Transaction
     *
     * Refund a transaction (full or partial).
     *
     * Args:
     *     provider_id: Payment provider to use
     *     request_data: Refund request with transaction ID and optional amount/reason
     *     payment_manager: Payment manager dependency
     *     db: Database session
     *
     * Returns:
     *     Refund transaction details
     *
     * @endpoint POST /payment/transactions/refund/{transaction_id}
     */
    refund: async ({
      transactionId,
      refundRequest,
    }: { transactionId?: string; refundRequest?: RefundRequest } = {}): Promise<
      AxiosResponse<RefundResponse>
    > => {
      return axios.post(
        `/payment/transactions/refund/${transactionId}`,
        refundRequest,
      );
    },
  },
  recurringPayments: {
    /**
     * Update recurring payment
     *
     * Update a recurring payment
     *
     * @endpoint PATCH /payment/recurring-payments/{recurring_payment_id}
     */
    patch: async ({
      recurringPaymentId,
      recurringPaymentUpdateRequest,
    }: {
      recurringPaymentId?: string;
      recurringPaymentUpdateRequest?: RecurringPaymentUpdateRequest;
    } = {}): Promise<AxiosResponse<RecurringPaymentResponse>> => {
      return axios.patch(
        `/payment/recurring-payments/${recurringPaymentId}`,
        recurringPaymentUpdateRequest,
      );
    },
    /**
     * Delete recurring payment
     *
     * Delete a recurring payment
     *
     * @endpoint DELETE /payment/recurring-payments/{recurring_payment_id}
     */
    delete: async ({
      recurringPaymentId,
    }: { recurringPaymentId?: string } = {}): Promise<
      AxiosResponse<RecurringPaymentDeleteResponse>
    > => {
      return axios.delete(`/payment/recurring-payments/${recurringPaymentId}`);
    },
    /**
     * Get recurring payment
     *
     * Get recurring payment details
     *
     * @endpoint GET /payment/recurring-payments/{recurring_payment_id}
     */
    getByRecurringPaymentId: async ({
      recurringPaymentId,
    }: { recurringPaymentId?: string } = {}): Promise<
      AxiosResponse<RecurringPaymentResponse>
    > => {
      return axios.get(`/payment/recurring-payments/${recurringPaymentId}`);
    },
    /**
     * List All Recurring Payments
     *
     * List all recurring payments with optional filtering.
     *
     * @endpoint GET /payment/recurring-payments
     */
    get: async ({
      personId,
      status,
      startDate,
      endDate,
      searchQuery,
      interval,
      limit = 50,
      offset,
    }: {
      personId?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
      searchQuery?: string;
      interval?: RecurringPaymentInterval;
      limit?: number;
      offset?: number;
    } = {}): Promise<AxiosResponse<RecurringPaymentResponse[]>> => {
      return axios.get("/payment/recurring-payments", {
        params: {
          person_id: personId,
          status,
          start_date: startDate,
          end_date: endDate,
          search_query: searchQuery,
          interval,
          limit,
          offset,
        },
      });
    },
    /**
     * Create recurring payment
     *
     * Create a new recurring payment using internal management
     *
     * @endpoint POST /payment/recurring-payments
     */
    post: async ({
      recurringPaymentCreateRequest,
    }: {
      recurringPaymentCreateRequest?: RecurringPaymentCreateRequest;
    } = {}): Promise<AxiosResponse<RecurringPaymentResponse>> => {
      return axios.post(
        "/payment/recurring-payments",
        recurringPaymentCreateRequest,
      );
    },
    /**
     * Get Recurring Payment Stats
     *
     * Get recurring payment stats (MRR/ARR and counts).
     *
     * @endpoint GET /payment/recurring-payments/stats
     */
    stats: async (): Promise<AxiosResponse<RecurringPaymentStatsResponse>> => {
      return axios.get("/payment/recurring-payments/stats");
    },
    /**
     * Cancel recurring payment
     *
     * Cancel a recurring payment
     *
     * @endpoint POST /payment/recurring-payments/{recurring_payment_id}/cancel
     */
    cancel: async ({
      recurringPaymentId,
      atPeriodEnd = true,
    }: { recurringPaymentId?: string; atPeriodEnd?: boolean } = {}): Promise<
      AxiosResponse<RecurringPaymentCancelResponse>
    > => {
      return axios.post(
        `/payment/recurring-payments/${recurringPaymentId}/cancel`,
        {},
        { params: { at_period_end: atPeriodEnd } },
      );
    },
    /**
     * Process recurring payment billing
     *
     * Process billing for recurring payments due today
     *
     * @endpoint POST /payment/recurring-payments/batch-billing
     */
    batchBilling: async ({
      limit,
      dryRun,
    }: { limit?: number; dryRun?: boolean } = {}): Promise<
      AxiosResponse<RecurringPaymentBillingResult>
    > => {
      return axios.post(
        "/payment/recurring-payments/batch-billing",
        {},
        { params: { limit, dry_run: dryRun } },
      );
    },
    /**
     * Charge a specific recurring payment
     *
     * Manually charge a single recurring payment by ID
     *
     * @endpoint POST /payment/recurring-payments/{recurring_payment_id}/charge
     */
    charge: async ({
      recurringPaymentId,
    }: { recurringPaymentId?: string } = {}): Promise<
      AxiosResponse<RecurringPaymentChargeResponse>
    > => {
      return axios.post(
        `/payment/recurring-payments/${recurringPaymentId}/charge`,
        {},
      );
    },
  },
  /**
   * Charge Payment Method
   *
   * Charge a saved payment method.
   *
   * Args:
   *     payment_method_id: ID or token of the saved payment method to charge
   *     request_data: Charge request with amount, payer info, etc.
   *     payment_manager: Payment manager dependency
   *     db: Database session
   *
   * Returns:
   *     Transaction charge details
   *
   * @endpoint POST /payment/charge/{payment_method_id}
   */
  charge: async ({
    paymentMethodId,
    chargeTokenRequest,
  }: {
    paymentMethodId?: string;
    chargeTokenRequest?: ChargeTokenRequest;
  } = {}): Promise<AxiosResponse<TransactionChargeResponse>> => {
    return axios.post(`/payment/charge/${paymentMethodId}`, chargeTokenRequest);
  },
  /**
   * Verify Payment Intent
   *
   * Verify payment for a specific PaymentIntent using structured provider data.
   *
   * Flexible approach that accepts either internal ID or provider token:
   * 1. Client specifies which PaymentIntent they're verifying (UUID or provider token)
   * 2. System looks up PaymentIntent by ID or provider_payment_intent_id
   * 3. Provider ID comes from the PaymentIntent record
   * 4. Provider processes with validated data
   *
   * Args:
   *     payment_intent_id: Either the internal PaymentIntent UUID or the provider's token
   *     verification_data: Provider-specific verification data
   *
   * @endpoint POST /payment/verify/{payment_intent_id}
   */
  verify: async ({
    paymentIntentId,
    verificationData,
  }: {
    paymentIntentId?: string;
    verificationData?: { [key: string]: any };
  } = {}): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.post(`/payment/verify/${paymentIntentId}`, verificationData);
  },
  webhooks: {
    /**
     * List Webhook Events
     *
     * List webhook events with optional filtering.
     *
     * Args:
     *     db: Database session
     *     limit: Maximum number of events to return
     *     offset: Number of events to skip
     *     provider_id: Filter by provider
     *     processed: Filter by processed status
     *
     * Returns:
     *     List of webhook events
     *
     * @endpoint GET /payment/webhooks
     */
    get: async ({
      limit = 50,
      offset,
      providerId,
      processed,
      includeData,
    }: {
      limit?: number;
      offset?: number;
      providerId?: string;
      processed?: boolean;
      includeData?: boolean;
    } = {}): Promise<AxiosResponse<{ [key: string]: any }[]>> => {
      return axios.get("/payment/webhooks", {
        params: {
          limit,
          offset,
          provider_id: providerId,
          processed,
          include_data: includeData,
        },
      });
    },
    /**
     * Handle Webhook
     *
     * Handle webhooks from payment providers.
     *
     * @endpoint POST /payment/webhooks/{provider_id}
     */
    post: async ({ providerId }: { providerId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.post(`/payment/webhooks/${providerId}`, {});
    },
  },
  logs: {
    /**
     * Get Payment Log
     *
     * Get a specific payment log by ID.
     *
     * @endpoint GET /payment/logs/{log_id}
     */
    getByLogId: async ({ logId }: { logId?: string } = {}): Promise<
      AxiosResponse<PaymentLog>
    > => {
      return axios.get(`/payment/logs/${logId}`);
    },
    /**
     * List Payment Logs
     *
     * List payment logs with optional filtering.
     *
     * @endpoint GET /payment/logs
     */
    get: async ({
      providerId,
      paymentIntentId,
      transactionId,
      processed,
      limit = 50,
      offset,
    }: {
      providerId?: string;
      paymentIntentId?: string;
      transactionId?: string;
      processed?: boolean;
      limit?: number;
      offset?: number;
    } = {}): Promise<AxiosResponse<PaymentLog[]>> => {
      return axios.get("/payment/logs", {
        params: {
          provider_id: providerId,
          payment_intent_id: paymentIntentId,
          transaction_id: transactionId,
          processed,
          limit,
          offset,
        },
      });
    },
  },
  /**
   * List Providers
   *
   * List available payment providers and their capabilities.
   *
   * @endpoint GET /payment/providers
   */
  providers: async (): Promise<AxiosResponse<ProviderListResponse>> => {
    return axios.get("/payment/providers");
  },
  /**
   * Health Check
   *
   * Health check endpoint for the payment module.
   *
   * @endpoint GET /payment/health
   */
  health: async (): Promise<AxiosResponse<HealthCheckResponse>> => {
    return axios.get("/payment/health");
  },
  intents: {
    /**
     * Get Payment Intent Details
     *
     * Get detailed payment intent information (debug-friendly).
     *
     * @endpoint GET /payment/intents/{payment_intent_id}
     */
    getByPaymentIntentId: async ({
      paymentIntentId,
    }: { paymentIntentId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.get(`/payment/intents/${paymentIntentId}`);
    },
    /**
     * List Payment Intents
     *
     * List payment intents with optional filtering.
     *
     * Args:
     *     db: Database session
     *     limit: Maximum number of intents to return
     *     offset: Number of intents to skip
     *     status: Filter by status
     *     provider_id: Filter by provider
     *
     * Returns:
     *     List of payment intents
     *
     * @endpoint GET /payment/intents
     */
    get: async ({
      limit = 50,
      offset,
      status,
      providerId,
    }: {
      limit?: number;
      offset?: number;
      status?: string;
      providerId?: string;
    } = {}): Promise<AxiosResponse<PaymentIntentResponse[]>> => {
      return axios.get("/payment/intents", {
        params: { limit, offset, status, provider_id: providerId },
      });
    },
  },
  /**
   * Get Success Page
   *
   * Serve the success page.
   *
   * @endpoint GET /payment/success
   */
  success: async () => {
    return axios.get("/payment/success");
  },
  /**
   * Payplus Checkout Page Alias
   *
   * Serve the PayPlus embedded checkout page (alias for /checkout).
   *
   * @endpoint GET /payment/payplus
   */
  payplus: async () => {
    return axios.get("/payment/payplus");
  },
};

/**
 * API functions for membership endpoints
 */
export const membership = {
  /**
   * Get Membership Details
   *
   * Get detailed membership information including computed details.
   *
   * @endpoint GET /membership/{membership_id}
   */
  get: async ({ membershipId }: { membershipId?: string } = {}): Promise<
    AxiosResponse<MembershipWithDetailsResponse>
  > => {
    return axios.get(`/membership/${membershipId}`);
  },
  /**
   * Update Membership Route
   *
   * Update a membership.
   *
   * @endpoint PUT /membership/{membership_id}
   */
  put: async ({
    membershipId,
    membershipUpdate,
  }: {
    membershipId?: string;
    membershipUpdate?: MembershipUpdate;
  } = {}): Promise<AxiosResponse<MembershipResponse>> => {
    return axios.put(`/membership/${membershipId}`, membershipUpdate);
  },
  /**
   * Create Membership Route
   *
   * Create a new membership for a person.
   *
   * @endpoint POST /membership/
   */
  post: async ({
    membershipCreate,
  }: { membershipCreate?: MembershipCreate } = {}): Promise<
    AxiosResponse<MembershipResponse>
  > => {
    return axios.post("/membership/", membershipCreate);
  },
  /**
   * Get Membership Health
   *
   * Health check endpoint for the membership module.
   *
   * @endpoint GET /membership/health
   */
  health: async (): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.get("/membership/health");
  },
  plans: {
    /**
     * Get Membership Plan
     *
     * Get a specific membership plan.
     *
     * @endpoint GET /membership/plans/{plan_id}
     */
    getByPlanId: async ({ planId }: { planId?: string } = {}): Promise<
      AxiosResponse<MembershipPlanResponse>
    > => {
      return axios.get(`/membership/plans/${planId}`);
    },
    /**
     * Update Membership Plan Route
     *
     * Update a membership plan.
     *
     * @endpoint PUT /membership/plans/{plan_id}
     */
    put: async ({
      planId,
      membershipPlanUpdate,
    }: {
      planId?: string;
      membershipPlanUpdate?: MembershipPlanUpdate;
    } = {}): Promise<AxiosResponse<MembershipPlanResponse>> => {
      return axios.put(`/membership/plans/${planId}`, membershipPlanUpdate);
    },
    /**
     * Create Membership Plan Route
     *
     * Create a new membership plan.
     *
     * @endpoint POST /membership/plans
     */
    post: async ({
      membershipPlanCreate,
    }: { membershipPlanCreate?: MembershipPlanCreate } = {}): Promise<
      AxiosResponse<MembershipPlanResponse>
    > => {
      return axios.post("/membership/plans", membershipPlanCreate);
    },
    /**
     * Get Membership Plans
     *
     * Get all membership plans.
     *
     * @endpoint GET /membership/plans
     */
    get: async ({
      activeOnly = true,
      groupPlansOnly,
    }: { activeOnly?: boolean; groupPlansOnly?: boolean } = {}): Promise<
      AxiosResponse<MembershipPlanResponse[]>
    > => {
      return axios.get("/membership/plans", {
        params: { active_only: activeOnly, group_plans_only: groupPlansOnly },
      });
    },
  },
  groups: {
    /**
     * Get Membership Group
     *
     * Get a specific membership group with plan details.
     *
     * @endpoint GET /membership/groups/{group_id}
     */
    getByGroupId: async ({ groupId }: { groupId?: string } = {}): Promise<
      AxiosResponse<MembershipGroupWithPlanResponse>
    > => {
      return axios.get(`/membership/groups/${groupId}`);
    },
    /**
     * Update Membership Group Route
     *
     * Update a membership group.
     *
     * @endpoint PUT /membership/groups/{group_id}
     */
    put: async ({
      groupId,
      membershipGroupUpdate,
    }: {
      groupId?: string;
      membershipGroupUpdate?: MembershipGroupUpdate;
    } = {}): Promise<AxiosResponse<MembershipGroupResponse>> => {
      return axios.put(`/membership/groups/${groupId}`, membershipGroupUpdate);
    },
    /**
     * Create Membership Group Route
     *
     * Create a new membership group (organization/sponsor).
     *
     * @endpoint POST /membership/groups
     */
    post: async ({
      membershipGroupCreate,
    }: { membershipGroupCreate?: MembershipGroupCreate } = {}): Promise<
      AxiosResponse<MembershipGroupResponse>
    > => {
      return axios.post("/membership/groups", membershipGroupCreate);
    },
    /**
     * Get Membership Groups
     *
     * Get all membership groups.
     *
     * @endpoint GET /membership/groups
     */
    get: async ({
      statusFilter,
      adminPersonId,
    }: {
      statusFilter?: MembershipStatus;
      adminPersonId?: string;
    } = {}): Promise<AxiosResponse<MembershipGroupResponse[]>> => {
      return axios.get("/membership/groups", {
        params: { status_filter: statusFilter, admin_person_id: adminPersonId },
      });
    },
    members: {
      /**
       * Remove Member From Group Route
       *
       * Remove a member from a group (frees one seat).
       *
       * @endpoint DELETE /membership/groups/{group_id}/members/{person_id}
       */
      delete: async ({
        groupId,
        personId,
        // removeRequest,
      }: {
        groupId?: string;
        personId?: string;
        // removeRequest?: RemoveMemberFromGroupRequest;
      } = {}): Promise<AxiosResponse<MembershipCancelResponse>> => {
        return axios.delete(
          `/membership/groups/${groupId}/members/${personId}`,
        );
      },
      /**
       * Add Member To Group Route
       *
       * Add a member to a group (uses one seat).
       *
       * @endpoint POST /membership/groups/{group_id}/members
       */
      post: async ({
        groupId,
        addMemberToGroupRequest,
      }: {
        groupId?: string;
        addMemberToGroupRequest?: AddMemberToGroupRequest;
      } = {}): Promise<AxiosResponse<MembershipResponse>> => {
        return axios.post(
          `/membership/groups/${groupId}/members`,
          addMemberToGroupRequest,
        );
      },
      /**
       * Get Group Members
       *
       * Get all members in a group.
       *
       * @endpoint GET /membership/groups/{group_id}/members
       */
      get: async ({
        groupId,
        activeOnly = true,
      }: { groupId?: string; activeOnly?: boolean } = {}): Promise<
        AxiosResponse<MembershipResponse[]>
      > => {
        return axios.get(`/membership/groups/${groupId}/members`, {
          params: { active_only: activeOnly },
        });
      },
    },
  },
  /**
   * Get Person Memberships
   *
   * Get all memberships for a specific person.
   *
   * @endpoint GET /membership/person/{person_id}
   */
  person: async ({
    personId,
    activeOnly,
  }: { personId?: string; activeOnly?: boolean } = {}): Promise<
    AxiosResponse<MembershipWithPlanResponse[]>
  > => {
    return axios.get(`/membership/person/${personId}`, {
      params: { active_only: activeOnly },
    });
  },
  /**
   * Cancel Membership Route
   *
   * Cancel a membership.
   *
   * @endpoint POST /membership/{membership_id}/cancel
   */
  cancel: async ({
    membershipId,
    membershipCancelRequest,
  }: {
    membershipId?: string;
    membershipCancelRequest?: MembershipCancelRequest;
  } = {}): Promise<AxiosResponse<MembershipCancelResponse>> => {
    return axios.post(
      `/membership/${membershipId}/cancel`,
      membershipCancelRequest,
    );
  },
  /**
   * Get Expiring Memberships
   *
   * Get memberships that are expiring soon.
   *
   * @endpoint POST /membership/expiring
   */
  expiring: async ({
    expiringMembershipsRequest,
  }: { expiringMembershipsRequest?: ExpiringMembershipsRequest } = {}): Promise<
    AxiosResponse<ExpiringMembershipsResponse>
  > => {
    return axios.post("/membership/expiring", expiringMembershipsRequest);
  },
  /**
   * Bulk Membership Action
   *
   * Perform bulk actions on multiple memberships.
   *
   * @endpoint POST /membership/bulk-action
   */
  bulkAction: async ({
    bulkMembershipAction,
  }: { bulkMembershipAction?: BulkMembershipAction } = {}): Promise<
    AxiosResponse<BulkMembershipResponse>
  > => {
    return axios.post("/membership/bulk-action", bulkMembershipAction);
  },
  /**
   * Check Entitlement
   *
   * Check if a person has a specific entitlement through their memberships.
   *
   * Examples:
   * - {person_id: "...", entitlement_key: "locations", entitlement_value: "downtown-gym"}
   * - {person_id: "...", entitlement_key: "features", entitlement_value: "pool"}
   * - {person_id: "...", entitlement_key: "max_devices"}
   *
   * @endpoint POST /membership/check-entitlement
   */
  checkEntitlement: async ({
    entitlementCheckRequest,
  }: { entitlementCheckRequest?: EntitlementCheckRequest } = {}): Promise<
    AxiosResponse<EntitlementCheckResponse>
  > => {
    return axios.post("/membership/check-entitlement", entitlementCheckRequest);
  },
};

/**
 * API functions for email endpoints
 */
export const email = {
  accounts: {
    /**
     * Remove Email Account
     *
     * Remove an email account.
     *
     * @endpoint DELETE /email/accounts/{email}
     */
    delete: async ({ email }: { email?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/email/accounts/${email}`);
    },
    /**
     * Add Email Account
     *
     * Add a new email account.
     *
     * @endpoint POST /email/accounts
     */
    post: async ({
      emailAccount,
    }: { emailAccount?: EmailAccount } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.post("/email/accounts", emailAccount);
    },
    /**
     * List Emails
     *
     * List emails for an account.
     *
     * @endpoint GET /email/accounts/{account_email}/emails
     */
    emails: async ({
      accountEmail,
      limit = 100,
    }: { accountEmail?: string; limit?: number } = {}): Promise<
      AxiosResponse<EmailMessage[]>
    > => {
      return axios.get(`/email/accounts/${accountEmail}/emails`, {
        params: { limit },
      });
    },
    /**
     * Check New Emails
     *
     * Check for new emails for an account.
     *
     * @endpoint POST /email/accounts/{account_email}/check
     */
    check: async ({ accountEmail }: { accountEmail?: string } = {}): Promise<
      AxiosResponse<EmailMessage[]>
    > => {
      return axios.post(`/email/accounts/${accountEmail}/check`, {});
    },
  },
  templates: {
    /**
     * Get Template
     *
     * Get a specific email template.
     *
     * @endpoint GET /email/templates/{template_name}
     */
    getByTemplateName: async ({
      templateName,
    }: { templateName?: string } = {}): Promise<
      AxiosResponse<EmailTemplate>
    > => {
      return axios.get(`/email/templates/${templateName}`);
    },
    /**
     * List Templates
     *
     * List all email templates.
     *
     * @endpoint GET /email/templates
     */
    get: async (): Promise<AxiosResponse<EmailTemplate[]>> => {
      return axios.get("/email/templates");
    },
    /**
     * Add Email Template
     *
     * Add a new email template.
     *
     * @endpoint POST /email/templates
     */
    post: async ({
      emailTemplate,
    }: { emailTemplate?: EmailTemplate } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.post("/email/templates", emailTemplate);
    },
  },
  /**
   * Send Email
   *
   * Send an email using a template.
   *
   * @endpoint POST /email/send
   */
  send: async ({
    accountEmail,
    templateName,
    sendEmailEmailSend,
  }: {
    accountEmail?: string;
    templateName?: string;
    sendEmailEmailSend?: SendEmailEmailSend;
  } = {}): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.post("/email/send", sendEmailEmailSend, {
      params: { account_email: accountEmail, template_name: templateName },
    });
  },
  /**
   * Get Email
   *
   * Get a specific email by ID.
   *
   * @endpoint GET /email/emails/{email_id}
   */
  emails: async ({ emailId }: { emailId?: string } = {}): Promise<
    AxiosResponse<EmailMessage>
  > => {
    return axios.get(`/email/emails/${emailId}`);
  },
};

/**
 * API functions for pipeline endpoints
 */
export const pipeline = {
  /**
   * Get Pipeline
   *
   * Get a pipeline by ID.
   *
   * @endpoint GET /pipeline/{pipeline_id}
   */
  getByPipelineId: async ({
    pipelineId,
  }: { pipelineId?: string } = {}): Promise<AxiosResponse<PipelineRead>> => {
    return axios.get(`/pipeline/${pipelineId}`);
  },
  /**
   * Update Pipeline
   *
   * Update an existing pipeline.
   *
   * @endpoint PUT /pipeline/{pipeline_id}
   */
  put: async ({
    pipelineId,
    pipelineBase,
  }: { pipelineId?: string; pipelineBase?: PipelineBase } = {}): Promise<
    AxiosResponse<PipelineRead>
  > => {
    return axios.put(`/pipeline/${pipelineId}`, pipelineBase);
  },
  /**
   * Delete Pipeline
   *
   * Delete a pipeline.
   *
   * @endpoint DELETE /pipeline/{pipeline_id}
   */
  delete: async ({ pipelineId }: { pipelineId?: string } = {}): Promise<
    AxiosResponse<PipelineRead>
  > => {
    return axios.delete(`/pipeline/${pipelineId}`);
  },
  /**
   * List Pipelines
   *
   * List all pipelines with pagination.
   *
   * @endpoint GET /pipeline/
   */
  get: async ({
    offset,
    limit = 100,
  }: { offset?: number; limit?: number } = {}): Promise<
    AxiosResponse<PipelineRead[]>
  > => {
    return axios.get("/pipeline/", { params: { offset, limit } });
  },
  /**
   * Create Pipeline
   *
   * Create a new pipeline.
   *
   * @endpoint POST /pipeline/
   */
  post: async ({ pipelineBase }: { pipelineBase?: PipelineBase } = {}): Promise<
    AxiosResponse<PipelineRead>
  > => {
    return axios.post("/pipeline/", pipelineBase);
  },
  stages: {
    /**
     * Update Stage
     *
     * Update an existing stage.
     *
     * @endpoint PUT /pipeline/stages/{stage_id}
     */
    put: async ({
      stageId,
      createStageRequest,
    }: {
      stageId?: string;
      createStageRequest?: CreateStageRequest;
    } = {}): Promise<AxiosResponse<StageRead>> => {
      return axios.put(`/pipeline/stages/${stageId}`, createStageRequest);
    },
    /**
     * Delete Stage
     *
     * Delete an existing stage.
     *
     * @endpoint DELETE /pipeline/stages/{stage_id}
     */
    delete: async ({ stageId }: { stageId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/pipeline/stages/${stageId}`);
    },
    /**
     * Create Stage
     *
     * Create a new stage in a pipeline.
     *
     * @endpoint POST /pipeline/{pipeline_id}/stages
     */
    post: async ({
      pipelineId,
      createStageRequest,
    }: {
      pipelineId?: string;
      createStageRequest?: CreateStageRequest;
    } = {}): Promise<AxiosResponse<StageRead>> => {
      return axios.post(`/pipeline/${pipelineId}/stages`, createStageRequest);
    },
    /**
     * List Stages
     *
     * List all stages in a pipeline.
     *
     * @endpoint GET /pipeline/{pipeline_id}/stages
     */
    get: async ({
      pipelineId,
      offset,
      limit = 50,
    }: { pipelineId?: string; offset?: number; limit?: number } = {}): Promise<
      AxiosResponse<StageRead[]>
    > => {
      return axios.get(`/pipeline/${pipelineId}/stages`, {
        params: { offset, limit },
      });
    },
    /**
     * List Items
     *
     * List all items in a stage, optionally filtered by tags.
     *
     * @endpoint GET /pipeline/stages/{stage_id}/items
     */
    items: async ({
      stageId,
      offset,
      limit = 50,
    }: { stageId?: string; offset?: number; limit?: number } = {}): Promise<
      AxiosResponse<PipelineItemRead[]>
    > => {
      return axios.get(`/pipeline/stages/${stageId}/items`, {
        params: { offset, limit },
      });
    },
  },
  items: {
    /**
     * Create Pipeline Item
     *
     * Create a new item in a pipeline.
     *
     * @endpoint POST /pipeline/{pipeline_id}/items
     */
    post: async ({
      pipelineId,
      createItemRequest,
    }: {
      pipelineId?: string;
      createItemRequest?: CreateItemRequest;
    } = {}): Promise<AxiosResponse<PipelineItemRead>> => {
      return axios.post(`/pipeline/${pipelineId}/items`, createItemRequest);
    },
    /**
     * List Pipeline Items
     *
     * List all items in a pipeline, optionally filtered by tags.
     *
     * @endpoint GET /pipeline/{pipeline_id}/items
     */
    getByPipelineId: async ({
      pipelineId,
      refId,
      tag = [],
      offset,
      limit = 50,
    }: {
      pipelineId?: string;
      refId?: string;
      tag?: string[];
      offset?: number;
      limit?: number;
    } = {}): Promise<AxiosResponse<PipelineItemRead[]>> => {
      return axios.get(`/pipeline/${pipelineId}/items`, {
        params: { ref_id: refId, tag, offset, limit },
      });
    },
    /**
     * Delete Item
     *
     * Delete an existing item.
     *
     * @endpoint DELETE /pipeline/items/{item_id}
     */
    delete: async ({ itemId }: { itemId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/pipeline/items/${itemId}`);
    },
    /**
     * Get Item
     *
     * Get an item by ID.
     *
     * @endpoint GET /pipeline/items/{item_id}
     */
    get: async ({ itemId }: { itemId?: string } = {}): Promise<
      AxiosResponse<PipelineItemRead>
    > => {
      return axios.get(`/pipeline/items/${itemId}`);
    },
    /**
     * Update Item
     *
     * Update an item.
     *
     * @endpoint PUT /pipeline/items/{item_id}
     */
    put: async ({
      itemId,
      updateItemRequest,
    }: {
      itemId?: string;
      updateItemRequest?: UpdateItemRequest;
    } = {}): Promise<AxiosResponse<PipelineItemRead>> => {
      return axios.put(`/pipeline/items/${itemId}`, updateItemRequest);
    },
    /**
     * Move Item
     *
     * Move an item to a different stage.
     *
     * @endpoint POST /pipeline/items/{item_id}/move
     */
    move: async ({
      itemId,
      moveItemRequest,
    }: { itemId?: string; moveItemRequest?: MoveItemRequest } = {}): Promise<
      AxiosResponse<PipelineItemRead>
    > => {
      return axios.post(`/pipeline/items/${itemId}/move`, moveItemRequest);
    },
    tags: {
      /**
       * Add Tag To Item
       *
       * Add a tag to an item.
       *
       * @endpoint POST /pipeline/items/{item_id}/tags/{tag_id}
       */
      post: async ({
        itemId,
        tagId,
      }: { itemId?: string; tagId?: string } = {}): Promise<
        AxiosResponse<TagActionResponse>
      > => {
        return axios.post(`/pipeline/items/${itemId}/tags/${tagId}`, {});
      },
      /**
       * Remove Tag From Item
       *
       * Remove a tag from an item.
       *
       * @endpoint DELETE /pipeline/items/{item_id}/tags/{tag_id}
       */
      delete: async ({
        itemId,
        tagId,
      }: { itemId?: string; tagId?: string } = {}): Promise<
        AxiosResponse<TagActionResponse>
      > => {
        return axios.delete(`/pipeline/items/${itemId}/tags/${tagId}`);
      },
    },
  },
  tags: {
    /**
     * Update Tag
     *
     * Update a tag.
     *
     * @endpoint PUT /pipeline/tags/{tag_id}
     */
    put: async ({
      tagId,
      createTagRequest,
    }: { tagId?: string; createTagRequest?: CreateTagRequest } = {}): Promise<
      AxiosResponse<PipelineTag>
    > => {
      return axios.put(`/pipeline/tags/${tagId}`, createTagRequest);
    },
    /**
     * Delete Tag
     *
     * Delete a tag.
     *
     * @endpoint DELETE /pipeline/tags/{tag_id}
     */
    delete: async ({ tagId }: { tagId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/pipeline/tags/${tagId}`);
    },
    /**
     * Create Tag
     *
     * Create a new tag in a pipeline.
     *
     * @endpoint POST /pipeline/{pipeline_id}/tags
     */
    post: async ({
      pipelineId,
      createTagRequest,
    }: {
      pipelineId?: string;
      createTagRequest?: CreateTagRequest;
    } = {}): Promise<AxiosResponse<PipelineTag>> => {
      return axios.post(`/pipeline/${pipelineId}/tags`, createTagRequest);
    },
    /**
     * Get Pipeline Tags
     *
     * List all tags in a pipeline.
     *
     * @endpoint GET /pipeline/{pipeline_id}/tags
     */
    get: async ({
      pipelineId,
      offset,
      limit = 50,
    }: { pipelineId?: string; offset?: number; limit?: number } = {}): Promise<
      AxiosResponse<PipelineTag[]>
    > => {
      return axios.get(`/pipeline/${pipelineId}/tags`, {
        params: { offset, limit },
      });
    },
  },
};

/**
 * API functions for person endpoints
 */
export const person = {
  /**
   * Get Person
   *
   * Get a person by ID
   *
   * @endpoint GET /person/{person_id}
   */
  getByPersonId: async ({ personId }: { personId?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.get(`/person/${personId}`);
  },
  /**
   * Update Person
   *
   * Update a person's details
   *
   * @endpoint PUT /person/{person_id}
   */
  put: async ({
    personId,
    personCreate,
  }: { personId?: string; personCreate?: PersonCreate } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.put(`/person/${personId}`, personCreate);
  },
  /**
   * Delete Person
   *
   * Delete a person
   *
   * @endpoint DELETE /person/{person_id}
   */
  delete: async ({ personId }: { personId?: string } = {}): Promise<
    AxiosResponse<{ [key: string]: any }>
  > => {
    return axios.delete(`/person/${personId}`);
  },
  /**
   * Create New Person
   *
   * Find or create a person
   *
   * @endpoint POST /person
   */
  post: async ({ personCreate }: { personCreate?: PersonCreate } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.post("/person", personCreate);
  },
  /**
   * List Persons
   *
   * List persons with pagination and optional archive filtering
   *
   * @endpoint GET /person
   */
  get: async ({
    skip,
    limit = 100,
    includeArchived,
  }: {
    skip?: number;
    limit?: number;
    includeArchived?: boolean;
  } = {}): Promise<AxiosResponse<PersonWithRelations[]>> => {
    return axios.get("/person", {
      params: { skip, limit, include_archived: includeArchived },
    });
  },
  /**
   * Get Person Role Options
   *
   * Get the available person roles
   *
   * @endpoint GET /person/roles
   */
  roles: async (): Promise<AxiosResponse<{ [key: string]: any }>> => {
    return axios.get("/person/roles");
  },
  /**
   * Find Or Create Person With Email
   *
   * Create a new person
   *
   * @endpoint POST /person/find_or_create_person_with_email
   */
  findOrCreatePersonWithEmail: async ({
    personWithEmail,
  }: { personWithEmail?: PersonWithEmail } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.post(
      "/person/find_or_create_person_with_email",
      personWithEmail,
    );
  },
  /**
   * Find Or Create User Person
   *
   * Create a new person
   *
   * @endpoint POST /person/find_or_create_person_from_user
   */
  findOrCreatePersonFromUser: async ({
    personBase,
  }: { personBase?: PersonBase } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.post("/person/find_or_create_person_from_user", personBase);
  },
  /**
   * Get Person By User Id
   *
   * Get a person by email
   *
   * @endpoint GET /person/by_user/{user_id}
   */
  byUser: async ({ userId }: { userId?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.get(`/person/by_user/${userId}`);
  },
  /**
   * Get By Email
   *
   * Get a person by email
   *
   * @endpoint GET /person/by_email/{email}
   */
  byEmail: async ({ email }: { email?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations | null>
  > => {
    return axios.get(`/person/by_email/${email}`);
  },
  /**
   * Get By Phone
   *
   * Get a person by person ID
   *
   * @endpoint GET /person/by_phone/{phone}
   */
  byPhone: async ({ phone }: { phone?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations | null>
  > => {
    return axios.get(`/person/by_phone/${phone}`);
  },
  /**
   * Get By National Id
   *
   * Get a person by national ID
   *
   * @endpoint GET /person/by_national_id/{national_id}
   */
  byNationalId: async ({ nationalId }: { nationalId?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.get(`/person/by_national_id/${nationalId}`);
  },
  /**
   * Archive Person
   *
   * Archive a person
   *
   * @endpoint PUT /person/{person_id}/archive
   */
  archive: async ({ personId }: { personId?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.put(`/person/${personId}/archive`, {});
  },
  /**
   * Unarchive Person
   *
   * Unarchive a person
   *
   * @endpoint PUT /person/{person_id}/unarchive
   */
  unarchive: async ({ personId }: { personId?: string } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.put(`/person/${personId}/unarchive`, {});
  },
  email: {
    /**
     * Delete Email
     *
     * @endpoint DELETE /person/email/{email_id}
     */
    delete: async ({ emailId }: { emailId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/person/email/${emailId}`);
    },
    /**
     * Add Email
     *
     * Add an email address to a person
     *
     * @endpoint POST /person/{person_id}/email
     */
    post: async ({
      personId,
      emailAddress,
    }: { personId?: string; emailAddress?: string } = {}): Promise<
      AxiosResponse<Email>
    > => {
      return axios.post(
        `/person/${personId}/email`,
        {},
        { params: { email_address: emailAddress } },
      );
    },
    /**
     * Verify Email
     *
     * Mark an email as verified
     *
     * @endpoint PUT /person/email/{email_id}/verify
     */
    verify: async ({ emailId }: { emailId?: string } = {}): Promise<
      AxiosResponse<Email>
    > => {
      return axios.put(`/person/email/${emailId}/verify`, {});
    },
    /**
     * Set Primary Email
     *
     * Set an email as the primary email for a person
     *
     * @endpoint PUT /person/email/{email_id}/set-primary
     */
    setPrimary: async ({ emailId }: { emailId?: string } = {}): Promise<
      AxiosResponse<PersonWithRelations>
    > => {
      return axios.put(`/person/email/${emailId}/set-primary`, {});
    },
  },
  phone: {
    /**
     * Delete Phone
     *
     * @endpoint DELETE /person/phone/{phone_id}
     */
    delete: async ({ phoneId }: { phoneId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/person/phone/${phoneId}`);
    },
    /**
     * Add Phone
     *
     * Add a phone number to a person
     *
     * @endpoint POST /person/{person_id}/phone
     */
    post: async ({
      personId,
      phoneNumber,
      isPrimary,
    }: {
      personId?: string;
      phoneNumber?: string;
      isPrimary?: boolean;
    } = {}): Promise<AxiosResponse<Phone>> => {
      return axios.post(
        `/person/${personId}/phone`,
        {},
        { params: { phone_number: phoneNumber, is_primary: isPrimary } },
      );
    },
    /**
     * Verify Phone
     *
     * Mark a phone as verified
     *
     * @endpoint PUT /person/phone/{phone_id}/verify
     */
    verify: async ({ phoneId }: { phoneId?: string } = {}): Promise<
      AxiosResponse<Phone>
    > => {
      return axios.put(`/person/phone/${phoneId}/verify`, {});
    },
    /**
     * Set Primary Phone
     *
     * Set a phone as the primary phone for a person
     *
     * @endpoint PUT /person/phone/{phone_id}/set-primary
     */
    setPrimary: async ({ phoneId }: { phoneId?: string } = {}): Promise<
      AxiosResponse<PersonWithRelations>
    > => {
      return axios.put(`/person/phone/${phoneId}/set-primary`, {});
    },
  },
  address: {
    /**
     * Update Address Route
     *
     * Update an address
     *
     * @endpoint PUT /person/address/{address_id}
     */
    put: async ({
      addressId,
      addressWrite,
    }: { addressId?: string; addressWrite?: AddressWrite } = {}): Promise<
      AxiosResponse<Address>
    > => {
      return axios.put(`/person/address/${addressId}`, addressWrite);
    },
    /**
     * Delete Address
     *
     * @endpoint DELETE /person/address/{address_id}
     */
    delete: async ({ addressId }: { addressId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/person/address/${addressId}`);
    },
    /**
     * Add Address
     *
     * Add an address to a person
     *
     * @endpoint POST /person/{person_id}/address
     */
    post: async ({
      personId,
      address,
    }: { personId?: string; address?: Address } = {}): Promise<
      AxiosResponse<Address>
    > => {
      return axios.post(`/person/${personId}/address`, address);
    },
  },
  /**
   * Search
   *
   * Search for a person by email, phone, or national ID with support for multipart search.
   *
   * @endpoint GET /person/search/{query}
   */
  search: async ({
    query,
    skip,
    limit,
    includeArchived,
  }: {
    query?: string;
    skip?: number;
    limit?: number;
    includeArchived?: boolean;
  } = {}): Promise<AxiosResponse<PersonWithRelations[]>> => {
    return axios.get(`/person/search/${query}`, {
      params: { skip, limit, include_archived: includeArchived },
    });
  },
  /**
   * Find Duplicates
   *
   * Find potential duplicate persons based on:
   * - Same email addresses
   * - Same phone numbers
   * - Similar names (exact first+last name match)
   * - Same national ID
   *
   * Returns groups of duplicates with the oldest person as the primary.
   *
   * @endpoint GET /person/find-duplicates
   */
  findDuplicates: async ({
    skip,
    limit,
  }: { skip?: number; limit?: number } = {}): Promise<
    AxiosResponse<DuplicateGroup[]>
  > => {
    return axios.get("/person/find-duplicates", { params: { skip, limit } });
  },
  /**
   * Merge Persons Route
   *
   * Merge duplicate persons into a primary person.
   *
   * This endpoint:
   * - Updates all foreign key references from duplicates to primary
   * - Transfers all emails, phones, and addresses from duplicates to primary
   * - Merges roles from all persons
   * - Archives duplicate persons and marks them with merged_into_id
   *
   * Note: Duplicates are archived rather than deleted to preserve audit trails.
   *
   * @endpoint POST /person/merge
   */
  merge: async ({
    mergePersonsRequest,
  }: { mergePersonsRequest?: MergePersonsRequest } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.post("/person/merge", mergePersonsRequest);
  },
};

/**
 * API functions for advert endpoints
 */
export const advert = {
  /**
   * Read Advert
   *
   * @endpoint GET /advert/{advert_id}
   */
  get: async ({ advertId }: { advertId?: string } = {}): Promise<
    AxiosResponse<AdvertRead>
  > => {
    return axios.get(`/advert/${advertId}`);
  },
  /**
   * Put Advert
   *
   * @endpoint PUT /advert/{advert_id}
   */
  put: async ({
    advertId,
    advertBase,
  }: { advertId?: string; advertBase?: AdvertBase } = {}): Promise<
    AxiosResponse<AdvertRead>
  > => {
    return axios.put(`/advert/${advertId}`, advertBase);
  },
  /**
   * Delete Advert
   *
   * @endpoint DELETE /advert/{advert_id}
   */
  delete: async ({ advertId }: { advertId?: string } = {}) => {
    return axios.delete(`/advert/${advertId}`);
  },
  /**
   * Read Adverts
   *
   * @endpoint GET /advert/get_list
   */
  getList: async ({ onlyActive }: { onlyActive?: boolean } = {}): Promise<
    AxiosResponse<AdvertRead[]>
  > => {
    return axios.get("/advert/get_list", {
      params: { only_active: onlyActive },
    });
  },
  /**
   * New Advert
   *
   * @endpoint POST /advert/new
   */
  new_: async ({ advertBase }: { advertBase?: AdvertBase } = {}): Promise<
    AxiosResponse<AdvertRead>
  > => {
    return axios.post("/advert/new", advertBase);
  },
};

/**
 * API functions for ecommerce endpoints
 */
export const ecommerce = {
  /**
   * List Products
   *
   * @endpoint GET /ecommerce/list_products
   */
  listProducts: async ({
    offset,
    limit = 20,
  }: { offset?: number; limit?: number } = {}): Promise<
    AxiosResponse<ProductRead[]>
  > => {
    return axios.get("/ecommerce/list_products", { params: { offset, limit } });
  },
  product: {
    /**
     * Create Product
     *
     * @endpoint POST /ecommerce/product
     */
    post: async ({ productBase }: { productBase?: ProductBase } = {}): Promise<
      AxiosResponse<Product>
    > => {
      return axios.post("/ecommerce/product", productBase);
    },
    /**
     * Update Product
     *
     * @endpoint PUT /ecommerce/product/{product_id}
     */
    put: async ({
      productId,
      productUpdate,
    }: { productId?: string; productUpdate?: ProductUpdate } = {}): Promise<
      AxiosResponse<Product>
    > => {
      return axios.put(`/ecommerce/product/${productId}`, productUpdate);
    },
    /**
     * Delete Product
     *
     * @endpoint DELETE /ecommerce/product/{product_id}
     */
    delete: async ({ productId }: { productId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/ecommerce/product/${productId}`);
    },
    /**
     * Get Product
     *
     * @endpoint GET /ecommerce/product/{product_id}
     */
    get: async ({ productId }: { productId?: string } = {}): Promise<
      AxiosResponse<Product>
    > => {
      return axios.get(`/ecommerce/product/${productId}`);
    },
  },
  cart: {
    /**
     * Get Cart
     *
     * @endpoint GET /ecommerce/cart/{cart_id}
     */
    get: async ({ cartId }: { cartId?: string } = {}): Promise<
      AxiosResponse<CartWithItems>
    > => {
      return axios.get(`/ecommerce/cart/${cartId}`);
    },
    /**
     * Create Cart
     *
     * @endpoint POST /ecommerce/cart
     */
    post: async ({ customerId }: { customerId?: string } = {}): Promise<
      AxiosResponse<Cart>
    > => {
      return axios.post(
        "/ecommerce/cart",
        {},
        { params: { customer_id: customerId } },
      );
    },
    item: {
      /**
       * Remove Cart Item
       *
       * @endpoint DELETE /ecommerce/cart/{cart_id}/item/{item_id}
       */
      delete: async ({
        cartId,
        itemId,
      }: { cartId?: string; itemId?: string } = {}): Promise<
        AxiosResponse<{ [key: string]: any }>
      > => {
        return axios.delete(`/ecommerce/cart/${cartId}/item/${itemId}`);
      },
      /**
       * Update Cart Item
       *
       * @endpoint PUT /ecommerce/cart/{cart_id}/item/{item_id}
       */
      put: async ({
        cartId,
        itemId,
        quantity,
      }: { cartId?: string; itemId?: string; quantity?: number } = {}): Promise<
        AxiosResponse<CartItem>
      > => {
        return axios.put(
          `/ecommerce/cart/${cartId}/item/${itemId}`,
          {},
          { params: { quantity } },
        );
      },
      /**
       * Add Cart Item
       *
       * @endpoint POST /ecommerce/cart/{cart_id}/item
       */
      post: async ({
        cartId,
        productId,
        quantity = 1,
      }: {
        cartId?: string;
        productId?: string;
        quantity?: number;
      } = {}): Promise<AxiosResponse<CartItem>> => {
        return axios.post(
          `/ecommerce/cart/${cartId}/item`,
          {},
          { params: { product_id: productId, quantity } },
        );
      },
    },
    /**
     * Apply Coupon To Cart
     *
     * @endpoint POST /ecommerce/cart/{cart_id}/apply_coupon
     */
    applyCoupon: async ({
      cartId,
      couponCode,
    }: { cartId?: string; couponCode?: string } = {}): Promise<
      AxiosResponse<CartWithItems>
    > => {
      return axios.post(
        `/ecommerce/cart/${cartId}/apply_coupon`,
        {},
        { params: { coupon_code: couponCode } },
      );
    },
    /**
     * Remove Coupon From Cart
     *
     * @endpoint DELETE /ecommerce/cart/{cart_id}/remove_coupon
     */
    removeCoupon: async ({ cartId }: { cartId?: string } = {}): Promise<
      AxiosResponse<CartWithItems>
    > => {
      return axios.delete(`/ecommerce/cart/${cartId}/remove_coupon`);
    },
    /**
     * Associate Cart With Customer
     *
     * @endpoint PUT /ecommerce/cart/{cart_id}/associate
     */
    associate: async ({
      cartId,
      customerId,
    }: { cartId?: string; customerId?: string } = {}): Promise<
      AxiosResponse<Cart>
    > => {
      return axios.put(
        `/ecommerce/cart/${cartId}/associate`,
        {},
        { params: { customer_id: customerId } },
      );
    },
    /**
     * Get Cart By Customer Id
     *
     * @endpoint GET /ecommerce/cart/customer/{customer_id}
     */
    customer: async ({ customerId }: { customerId?: string } = {}): Promise<
      AxiosResponse<Cart>
    > => {
      return axios.get(`/ecommerce/cart/customer/${customerId}`);
    },
    /**
     * Checkout Cart
     *
     * Convert cart to order using all information already stored in the cart.
     * Customer is created/updated at this point if needed.
     *
     * @endpoint POST /ecommerce/cart/{cart_id}/checkout
     */
    checkout: async ({ cartId }: { cartId?: string } = {}): Promise<
      AxiosResponse<OrderRead>
    > => {
      return axios.post(`/ecommerce/cart/${cartId}/checkout`, {});
    },
    /**
     * Update Cart Shipping Info
     *
     * @endpoint PUT /ecommerce/cart/{cart_id}/shipping_info
     */
    shippingInfo: async ({
      cartId,
      shippingInformation,
    }: {
      cartId?: string;
      shippingInformation?: ShippingInformation;
    } = {}): Promise<AxiosResponse<CartWithItems>> => {
      return axios.put(
        `/ecommerce/cart/${cartId}/shipping_info`,
        shippingInformation,
      );
    },
    /**
     * Update Cart Shipping
     *
     * @endpoint PUT /ecommerce/cart/{cart_id}/shipping
     */
    shipping: async ({
      cartId,
      freeShipping,
      shippingOptionId,
    }: {
      cartId?: string;
      freeShipping?: boolean;
      shippingOptionId?: string;
    } = {}): Promise<AxiosResponse<CartWithItems>> => {
      return axios.put(
        `/ecommerce/cart/${cartId}/shipping`,
        {},
        {
          params: {
            free_shipping: freeShipping,
            shipping_option_id: shippingOptionId,
          },
        },
      );
    },
    /**
     * Get Cart Bundles
     *
     * Get information about bundles that can be applied to the cart.
     *
     * @endpoint GET /ecommerce/cart/{cart_id}/bundles
     */
    bundles: async ({ cartId }: { cartId?: string } = {}): Promise<
      AxiosResponse<AppliedBundleInfo[]>
    > => {
      return axios.get(`/ecommerce/cart/${cartId}/bundles`);
    },
  },
  shippingOptions: {
    /**
     * Update Shipping Option
     *
     * @endpoint PUT /ecommerce/shipping_options/{shipping_option_id}
     */
    put: async ({
      shippingOptionId,
      shippingOption,
    }: {
      shippingOptionId?: string;
      shippingOption?: ShippingOption;
    } = {}): Promise<AxiosResponse<ShippingOptionRead>> => {
      return axios.put(
        `/ecommerce/shipping_options/${shippingOptionId}`,
        shippingOption,
      );
    },
    /**
     * List Shipping Options
     *
     * @endpoint GET /ecommerce/shipping_options
     */
    get: async ({ activeOnly = true }: { activeOnly?: boolean } = {}): Promise<
      AxiosResponse<ShippingOptionRead[]>
    > => {
      return axios.get("/ecommerce/shipping_options", {
        params: { active_only: activeOnly },
      });
    },
    /**
     * Create Shipping Option
     *
     * @endpoint POST /ecommerce/shipping_options
     */
    post: async ({
      name,
      price,
      isActive = true,
      isDefault,
    }: {
      name?: string;
      price?: number;
      isActive?: boolean;
      isDefault?: boolean;
    } = {}): Promise<AxiosResponse<ShippingOptionRead>> => {
      return axios.post(
        "/ecommerce/shipping_options",
        {},
        { params: { name, price, is_active: isActive, is_default: isDefault } },
      );
    },
  },
  order: {
    /**
     * List Orders
     *
     * @endpoint GET /ecommerce/order
     */
    getAll: async ({
      status,
      customerId,
      offset,
      limit = 50,
    }: {
      status?: OrderStatus;
      customerId?: string;
      offset?: number;
      limit?: number;
    } = {}): Promise<AxiosResponse<OrderRead[]>> => {
      return axios.get("/ecommerce/order", {
        params: { status, customer_id: customerId, offset, limit },
      });
    },
    /**
     * Get Order
     *
     * @endpoint GET /ecommerce/order/{order_id}
     */
    get: async ({ orderId }: { orderId?: string } = {}): Promise<
      AxiosResponse<OrderRead>
    > => {
      return axios.get(`/ecommerce/order/${orderId}`);
    },
    /**
     * Delete Order
     *
     * @endpoint DELETE /ecommerce/order/{order_id}
     */
    delete: async ({ orderId }: { orderId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/ecommerce/order/${orderId}`);
    },
    /**
     * Update Order Status
     *
     * @endpoint PUT /ecommerce/order/{order_id}
     */
    put: async ({
      orderId,
      orderUpdate,
    }: { orderId?: string; orderUpdate?: OrderUpdate } = {}): Promise<
      AxiosResponse<Order>
    > => {
      return axios.put(`/ecommerce/order/${orderId}`, orderUpdate);
    },
    /**
     * Add Items To Order
     *
     * @endpoint POST /ecommerce/order/{order_id}/modify
     */
    modify: async ({
      orderId,
      itemsIds,
    }: { orderId?: string; itemsIds?: string[] } = {}): Promise<
      AxiosResponse<Order>
    > => {
      return axios.post(`/ecommerce/order/${orderId}/modify`, itemsIds);
    },
  },
  promotions: {
    /**
     * Update Promotion
     *
     * @endpoint PUT /ecommerce/promotions/{promotion_id}
     */
    put: async ({
      promotionId,
      promotion,
    }: { promotionId?: string; promotion?: Promotion } = {}): Promise<
      AxiosResponse<Promotion>
    > => {
      return axios.put(`/ecommerce/promotions/${promotionId}`, promotion);
    },
    /**
     * List Promotions
     *
     * @endpoint GET /ecommerce/promotions
     */
    get: async ({
      activeOnly = true,
      offset,
      limit = 20,
    }: { activeOnly?: boolean; offset?: number; limit?: number } = {}): Promise<
      AxiosResponse<Promotion[]>
    > => {
      return axios.get("/ecommerce/promotions", {
        params: { active_only: activeOnly, offset, limit },
      });
    },
    /**
     * Create Promotion
     *
     * @endpoint POST /ecommerce/promotions
     */
    post: async ({
      promotionBase,
    }: { promotionBase?: PromotionBase } = {}): Promise<
      AxiosResponse<Promotion>
    > => {
      return axios.post("/ecommerce/promotions", promotionBase);
    },
  },
  coupon: {
    /**
     * Validate Coupon
     *
     * @endpoint GET /ecommerce/coupon/{coupon_code}
     */
    getByCouponCode: async ({
      couponCode,
    }: { couponCode?: string } = {}): Promise<
      AxiosResponse<CouponValidationResponse>
    > => {
      return axios.get(`/ecommerce/coupon/${couponCode}`);
    },
    /**
     * Update Coupon
     *
     * @endpoint PUT /ecommerce/coupon/{coupon_id}
     */
    put: async ({
      couponId,
      coupon,
    }: { couponId?: string; coupon?: Coupon } = {}): Promise<
      AxiosResponse<Coupon>
    > => {
      return axios.put(`/ecommerce/coupon/${couponId}`, coupon);
    },
    /**
     * List Coupons
     *
     * @endpoint GET /ecommerce/coupon
     */
    get: async (): Promise<AxiosResponse<Coupon[]>> => {
      return axios.get("/ecommerce/coupon");
    },
    /**
     * Create Coupon
     *
     * @endpoint POST /ecommerce/coupon
     */
    post: async ({ couponBase }: { couponBase?: CouponBase } = {}): Promise<
      AxiosResponse<Coupon>
    > => {
      return axios.post("/ecommerce/coupon", couponBase);
    },
  },
  productCategory: {
    /**
     * Get Product Category
     *
     * @endpoint GET /ecommerce/product_category/{category_id}
     */
    getByCategoryId: async ({
      categoryId,
    }: { categoryId?: string } = {}): Promise<
      AxiosResponse<ProductCategory>
    > => {
      return axios.get(`/ecommerce/product_category/${categoryId}`);
    },
    /**
     * Update Product Category
     *
     * @endpoint PUT /ecommerce/product_category/{category_id}
     */
    put: async ({
      categoryId,
      productCategory,
    }: {
      categoryId?: string;
      productCategory?: ProductCategory;
    } = {}): Promise<AxiosResponse<ProductCategory>> => {
      return axios.put(
        `/ecommerce/product_category/${categoryId}`,
        productCategory,
      );
    },
    /**
     * Delete Product Category
     *
     * @endpoint DELETE /ecommerce/product_category/{category_id}
     */
    delete: async ({ categoryId }: { categoryId?: string } = {}): Promise<
      AxiosResponse<ProductCategory>
    > => {
      return axios.delete(`/ecommerce/product_category/${categoryId}`);
    },
    /**
     * List Product Categories
     *
     * @endpoint GET /ecommerce/product_category
     */
    get: async (): Promise<AxiosResponse<ProductCategory[]>> => {
      return axios.get("/ecommerce/product_category");
    },
    /**
     * Create Product Category
     *
     * @endpoint POST /ecommerce/product_category
     */
    post: async ({
      productCategory,
    }: { productCategory?: ProductCategory } = {}): Promise<
      AxiosResponse<ProductCategory>
    > => {
      return axios.post("/ecommerce/product_category", productCategory);
    },
  },
  bundle: {
    /**
     * Get Bundle
     *
     * @endpoint GET /ecommerce/bundle/{bundle_id}
     */
    getByBundleId: async ({ bundleId }: { bundleId?: string } = {}): Promise<
      AxiosResponse<BundleRead>
    > => {
      return axios.get(`/ecommerce/bundle/${bundleId}`);
    },
    /**
     * Update Bundle
     *
     * @endpoint PUT /ecommerce/bundle/{bundle_id}
     */
    put: async ({
      bundleId,
      bundleUpdate,
    }: { bundleId?: string; bundleUpdate?: BundleUpdate } = {}): Promise<
      AxiosResponse<BundleRead>
    > => {
      return axios.put(`/ecommerce/bundle/${bundleId}`, bundleUpdate);
    },
    /**
     * Delete Bundle
     *
     * @endpoint DELETE /ecommerce/bundle/{bundle_id}
     */
    delete: async ({ bundleId }: { bundleId?: string } = {}): Promise<
      AxiosResponse<{ [key: string]: any }>
    > => {
      return axios.delete(`/ecommerce/bundle/${bundleId}`);
    },
    /**
     * List Bundles
     *
     * @endpoint GET /ecommerce/bundle
     */
    get: async ({
      activeOnly = true,
      offset,
      limit = 20,
    }: { activeOnly?: boolean; offset?: number; limit?: number } = {}): Promise<
      AxiosResponse<BundleRead[]>
    > => {
      return axios.get("/ecommerce/bundle", {
        params: { active_only: activeOnly, offset, limit },
      });
    },
    /**
     * Create Bundle
     *
     * @endpoint POST /ecommerce/bundle
     */
    post: async ({
      bundleCreate,
    }: { bundleCreate?: BundleCreate } = {}): Promise<
      AxiosResponse<BundleRead>
    > => {
      return axios.post("/ecommerce/bundle", bundleCreate);
    },
  },
  analytics: {
    /**
     * Get Sales Overview
     *
     * Get overall sales statistics for a given date range.
     *
     * @endpoint GET /ecommerce/analytics/sales-overview
     */
    salesOverview: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<SalesOverviewAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/sales-overview", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Product Performance
     *
     * Get product performance metrics for a given date range.
     *
     * @endpoint GET /ecommerce/analytics/product-performance
     */
    productPerformance: async ({
      endDate,
      startDate,
      limit = 20,
    }: { endDate?: string; startDate?: string; limit?: number } = {}): Promise<
      AxiosResponse<ProductPerformanceAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/product-performance", {
        params: { end_date: endDate, start_date: startDate, limit },
      });
    },
    /**
     * Get Revenue Trends
     *
     * Get daily revenue trends for a given date range.
     *
     * @endpoint GET /ecommerce/analytics/revenue-trends
     */
    revenueTrends: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<RevenueTrendsAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/revenue-trends", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Daily Breakdown
     *
     * Get daily breakdown data optimized for charts and graphs.
     *
     * @endpoint GET /ecommerce/analytics/daily-breakdown
     */
    dailyBreakdown: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<DailyBreakdownAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/daily-breakdown", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Cart Analytics
     *
     * Get cart abandonment and conversion analytics.
     *
     * @endpoint GET /ecommerce/analytics/cart-analytics
     */
    cartAnalytics: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<CartAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/cart-analytics", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Coupon Performance
     *
     * Get coupon usage and effectiveness analytics.
     *
     * @endpoint GET /ecommerce/analytics/coupon-performance
     */
    couponPerformance: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<CouponPerformanceAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/coupon-performance", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Category Performance
     *
     * Get product category performance analytics.
     *
     * @endpoint GET /ecommerce/analytics/category-performance
     */
    categoryPerformance: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<CategoryPerformanceAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/category-performance", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Customer Analytics
     *
     * Get customer-related analytics.
     *
     * @endpoint GET /ecommerce/analytics/customer-analytics
     */
    customerAnalytics: async ({
      endDate,
      startDate,
    }: { endDate?: string; startDate?: string } = {}): Promise<
      AxiosResponse<CustomerAnalytics>
    > => {
      return axios.get("/ecommerce/analytics/customer-analytics", {
        params: { end_date: endDate, start_date: startDate },
      });
    },
    /**
     * Get Customer Specific Analytics
     *
     * Get detailed analytics for a specific customer.
     *
     * @endpoint GET /ecommerce/analytics/customer/{customer_id}
     */
    customer: async ({
      customerId,
      endDate,
      startDate,
    }: {
      customerId?: string;
      endDate?: string;
      startDate?: string;
    } = {}): Promise<AxiosResponse<CustomerAnalytics>> => {
      return axios.get(`/ecommerce/analytics/customer/${customerId}`, {
        params: { end_date: endDate, start_date: startDate },
      });
    },
  },
};

/**
 * API functions for authors endpoints
 */
export const authors = {
  /**
   * Read Author
   *
   * @endpoint GET /authors/{author_id}
   */
  getByAuthorId: async ({ authorId }: { authorId?: string } = {}): Promise<
    AxiosResponse<Author>
  > => {
    return axios.get(`/authors/${authorId}`);
  },
  /**
   * Update Author
   *
   * @endpoint PATCH /authors/{author_id}
   */
  patch: async ({
    authorId,
    authorBase,
  }: { authorId?: string; authorBase?: AuthorBase } = {}): Promise<
    AxiosResponse<Author>
  > => {
    return axios.patch(`/authors/${authorId}`, authorBase);
  },
  /**
   * Delete Author
   *
   * @endpoint DELETE /authors/{author_id}
   */
  delete: async ({ authorId }: { authorId?: string } = {}) => {
    return axios.delete(`/authors/${authorId}`);
  },
  /**
   * List Authors
   *
   * @endpoint GET /authors/
   */
  get: async (): Promise<AxiosResponse<Author[]>> => {
    return axios.get("/authors/");
  },
  /**
   * Create Author
   *
   * @endpoint POST /authors/
   */
  post: async ({ authorBase }: { authorBase?: AuthorBase } = {}): Promise<
    AxiosResponse<Author>
  > => {
    return axios.post("/authors/", authorBase);
  },
};

/**
 * API functions for digitalLib endpoints
 */
export const digitalLib = {
  /**
   * Read Items
   *
   * @endpoint POST /digital_lib/item_list
   */
  itemList: async ({ payload }: { payload?: LibPaginationReq } = {}): Promise<
    AxiosResponse<LibItemWithJoinsRes[]>
  > => {
    return axios.post("/digital_lib/item_list", payload);
  },
  /**
   * Read App Home Item List
   *
   * @endpoint POST /digital_lib/app_home_item_list
   */
  appHomeItemList: async ({
    getAvailableAppLibItemsReq,
  }: { getAvailableAppLibItemsReq?: GetAvailableAppLibItemsReq } = {}): Promise<
    AxiosResponse<GetAvailableAppLibItemsJoinRes>
  > => {
    return axios.post(
      "/digital_lib/app_home_item_list",
      getAvailableAppLibItemsReq,
    );
  },
  item: {
    /**
     * Read Item
     *
     * @endpoint GET /digital_lib/item/{item_id}
     */
    get: async ({ itemId }: { itemId?: string } = {}): Promise<
      AxiosResponse<LibItemWithJoinsRes>
    > => {
      return axios.get(`/digital_lib/item/${itemId}`);
    },
    /**
     * Edit Item
     *
     * @endpoint PUT /digital_lib/item/{item_id}
     */
    put: async ({
      itemId,
      libItemUpsert,
    }: { itemId?: string; libItemUpsert?: LibItemUpsert } = {}): Promise<
      AxiosResponse<LibItemWithJoinsRes>
    > => {
      return axios.put(`/digital_lib/item/${itemId}`, libItemUpsert);
    },
    /**
     * Remove Item
     *
     * @endpoint DELETE /digital_lib/item/{item_id}
     */
    delete: async ({ itemId }: { itemId?: string } = {}): Promise<
      AxiosResponse<LibItemWithJoinsRes>
    > => {
      return axios.delete(`/digital_lib/item/${itemId}`);
    },
    /**
     * Add Item
     *
     * @endpoint POST /digital_lib/item
     */
    post: async ({
      libItemUpsert,
    }: { libItemUpsert?: LibItemUpsert } = {}): Promise<
      AxiosResponse<LibItemWithJoinsRes>
    > => {
      return axios.post("/digital_lib/item", libItemUpsert);
    },
    /**
     * Add Collection
     *
     * @endpoint POST /digital_lib/item/{item_id}/collection
     */
    collection: async ({
      itemId,
      addCollectionReq,
    }: { itemId?: string; addCollectionReq?: AddCollectionReq } = {}) => {
      return axios.post(
        `/digital_lib/item/${itemId}/collection`,
        addCollectionReq,
      );
    },
  },
  /**
   * Read Raw Categories
   *
   * @endpoint GET /digital_lib/raw_category_list
   */
  rawCategoryList: async (): Promise<AxiosResponse<LibCategory[]>> => {
    return axios.get("/digital_lib/raw_category_list");
  },
  /**
   * Read Categories
   *
   * @endpoint GET /digital_lib/category_list
   */
  categoryList: async ({
    onlyFeatured = true,
  }: { onlyFeatured?: boolean } = {}): Promise<AxiosResponse<string[]>> => {
    return axios.get("/digital_lib/category_list", {
      params: { only_featured: onlyFeatured },
    });
  },
  categories: {
    /**
     * Add Category
     *
     * @endpoint POST /digital_lib/categories
     */
    post: async ({
      libCategoryBase,
    }: { libCategoryBase?: LibCategoryBase } = {}): Promise<
      AxiosResponse<LibCategory>
    > => {
      return axios.post("/digital_lib/categories", libCategoryBase);
    },
    /**
     * Read Category
     *
     * @endpoint GET /digital_lib/categories/{name}
     */
    get: async ({ name }: { name?: string } = {}): Promise<
      AxiosResponse<LibCategory>
    > => {
      return axios.get(`/digital_lib/categories/${name}`);
    },
    /**
     * Edit Category
     *
     * @endpoint PUT /digital_lib/categories/{name}
     */
    put: async ({
      libCategoryBase,
    }: { libCategoryBase?: LibCategoryBase } = {}): Promise<
      AxiosResponse<LibCategory>
    > => {
      return axios.put(`/digital_lib/categories/${name}`, libCategoryBase);
    },
    /**
     * Remove Category
     *
     * @endpoint DELETE /digital_lib/categories/{name}
     */
    delete: async ({ name }: { name?: string } = {}) => {
      return axios.delete(`/digital_lib/categories/${name}`);
    },
  },
  /**
   * Read Collection Items
   *
   * @endpoint GET /digital_lib/collection_item_list
   */
  collectionItemList: async (): Promise<
    AxiosResponse<LibCollectionItemBase[]>
  > => {
    return axios.get("/digital_lib/collection_item_list");
  },
  collectionItem: {
    /**
     * Add Collection Item
     *
     * @endpoint POST /digital_lib/collection_item
     */
    post: async ({
      libCollectionItem,
    }: { libCollectionItem?: LibCollectionItem } = {}): Promise<
      AxiosResponse<LibCollectionItemBase>
    > => {
      return axios.post("/digital_lib/collection_item", libCollectionItem);
    },
    /**
     * Read Collection Item
     *
     * @endpoint GET /digital_lib/collection_item/{item_id}
     */
    get: async ({ itemId }: { itemId?: string } = {}): Promise<
      AxiosResponse<LibCollectionItemBase>
    > => {
      return axios.get(`/digital_lib/collection_item/${itemId}`);
    },
    /**
     * Edit Collection Item
     *
     * @endpoint PUT /digital_lib/collection_item/{item_id}
     */
    put: async ({
      itemId,
      libCollectionItemBase,
    }: {
      itemId?: string;
      libCollectionItemBase?: LibCollectionItemBase;
    } = {}): Promise<AxiosResponse<LibCollectionItemBase>> => {
      return axios.put(
        `/digital_lib/collection_item/${itemId}`,
        libCollectionItemBase,
      );
    },
    /**
     * Remove Collection Item
     *
     * @endpoint DELETE /digital_lib/collection_item/{item_id}
     */
    delete: async ({ itemId }: { itemId?: string } = {}) => {
      return axios.delete(`/digital_lib/collection_item/${itemId}`);
    },
  },
  libItemLink: {
    person: {
      /**
       * Get Person With Lib Items
       *
       * @endpoint GET /digital_lib/lib_item_link/person/{person_id}
       */
      get: async ({ personId }: { personId?: string } = {}): Promise<
        AxiosResponse<PersonWithLibItems>
      > => {
        return axios.get(`/digital_lib/lib_item_link/person/${personId}`);
      },
      /**
       * Upsert Person Lib Item Connection
       *
       * @endpoint POST /digital_lib/lib_item_link/person
       */
      post: async ({
        personToLibItemLinkBase,
      }: { personToLibItemLinkBase?: PersonToLibItemLinkBase } = {}) => {
        return axios.post(
          "/digital_lib/lib_item_link/person",
          personToLibItemLinkBase,
        );
      },
      /**
       * Remove Person Lib Item Link
       *
       * @endpoint DELETE /digital_lib/lib_item_link/person
       */
      delete: async ({
        personId,
        libItemId,
      }: { personId?: string; libItemId?: string } = {}) => {
        return axios.delete("/digital_lib/lib_item_link/person", {
          params: { person_id: personId, lib_item_id: libItemId },
        });
      },
    },
    /**
     * Get User With Lib Items
     *
     * @endpoint GET /digital_lib/lib_item_link/user/{user_id}
     */
    user: async ({ userId }: { userId?: string } = {}): Promise<
      AxiosResponse<PersonWithLibItems>
    > => {
      return axios.get(`/digital_lib/lib_item_link/user/${userId}`);
    },
    /**
     * Update Progress
     *
     * Update reading and/or audio progress for library items
     *
     * @endpoint PUT /digital_lib/lib_item_link/progress
     */
    progress: async ({
      updateProgressReq,
    }: { updateProgressReq?: UpdateProgressReq } = {}) => {
      return axios.put(
        "/digital_lib/lib_item_link/progress",
        updateProgressReq,
      );
    },
  },
  /**
   * Signup
   *
   * @endpoint POST /digital_lib/signup
   */
  signup: async ({ signupReq }: { signupReq?: SignupReq } = {}): Promise<
    AxiosResponse<PersonWithRelations>
  > => {
    return axios.post("/digital_lib/signup", signupReq);
  },
};

/**
 * API functions for sellameirPayment endpoints
 */
export const sellameirPayment = {
  /**
   * Create Person Subscription Link
   *
   * @endpoint GET /sellameir_payment/create_person_subscription_link
   */
  createPersonSubscriptionLink: async ({
    personId,
    subscriptionId,
  }: { personId?: string; subscriptionId?: string } = {}): Promise<
    AxiosResponse<PersonSubscriptionLink>
  > => {
    return axios.get("/sellameir_payment/create_person_subscription_link", {
      params: { person_id: personId, subscription_id: subscriptionId },
    });
  },
  /**
   * Get Subscription
   *
   * @endpoint GET /sellameir_payment/subscription/{person_id}
   */
  subscription: async ({ personId }: { personId?: string } = {}): Promise<
    AxiosResponse<Subscription[]>
  > => {
    return axios.get(`/sellameir_payment/subscription/${personId}`);
  },
  /**
   * Confirm Order
   *
   * @endpoint POST /sellameir_payment/confirm_order/{payment_request_id}
   */
  confirmOrder: async ({
    paymentRequestId,
  }: { paymentRequestId?: string } = {}): Promise<
    AxiosResponse<{ [key: string]: any }>
  > => {
    return axios.post(
      `/sellameir_payment/confirm_order/${paymentRequestId}`,
      {},
    );
  },
  /**
   * Confirm All Orders
   *
   * @endpoint POST /sellameir_payment/confirm_all_orders
   */
  confirmAllOrders: async (): Promise<
    AxiosResponse<{ [key: string]: any }>
  > => {
    return axios.post("/sellameir_payment/confirm_all_orders", {});
  },
  /**
   * Fix Payplus Callback
   *
   * Fix PayPlus callback by updating token_uid for paid payment requests.
   *
   * @endpoint POST /sellameir_payment/fix_payplus_callback
   */
  fixPayplusCallback: async (): Promise<
    AxiosResponse<{ [key: string]: any }>
  > => {
    return axios.post("/sellameir_payment/fix_payplus_callback", {});
  },
};
